/* eslint-disable react/require-default-props */
import React, {ReactNode, useMemo, useRef} from 'react';
import {
  AccessibilityRole,
  LayoutChangeEvent,
  PixelRatio,
  Platform,
  View,
  ViewStyle,
} from 'react-native';
import {isEmpty, noop} from 'lodash';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {getTextFromReactNode} from '/util/accessibility.util';
import {generateCallback} from '/util/component.util';
import {useAccessibilityFocus} from '/util/hooks/useAccessibilityFocus.hook';

import KeyboardFocusable from '../ExtKeyboard/KeyboardFocusable';

import {styles} from './ProfileActiveAttribute.style';

interface ProfileActiveAttributeProps {
  style?: {
    base?: ViewStyle;
    arrowIcon?: ViewStyle;
  };
  iconName?: string;
  isLastElement?: boolean;
  label?: string | ReactNode;
  useCompetency?: boolean;
  onPress?: (payload: {
    triggerRef: React.RefObject<any>;
    [key: string]: any;
  }) => void;
  testID?: string | null;
  onLayout?: (event: LayoutChangeEvent) => void;
  accessibilityRole?: AccessibilityRole;
  accessibilityLabel?: string;
  accessible?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  status?: string;
  accessibilityHint?: string;
  disabled?: boolean;
  isProfileBusinessSection?: boolean;
}

export const ProfileActiveAttribute: React.FC<ProfileActiveAttributeProps> = ({
  style = {},
  iconName = '',
  isLastElement = false,
  label = '',
  useCompetency = false,
  onPress = noop,
  onLayout,
  accessibilityRole = 'button',
  accessibilityLabel = '',
  accessible = true,
  accessibilityHint = '',
  onFocus,
  onBlur,
  status = '',
  testID = '',
  disabled = false,
  isProfileBusinessSection,
}) => {
  const internalRef = useRef(null);
  const isScreenReaderEnabled = useAccessibilityFocus(internalRef);

  const fontScale = PixelRatio.getFontScale();

  const scaledIcon = fontScale > 1.3 ? 26 : 24;

  const scaledGlobeIcon = fontScale > 1.3 ? 26 : 24;

  const scaledArrowIcon = fontScale > 1.3 ? 20 : 16;

  const labelText = useMemo(() => getTextFromReactNode(label) || '', [label]);
  const normalizedStatus = typeof status === 'string' ? status.trim() : '';

  const shouldShowStatus =
    !!normalizedStatus && normalizedStatus !== labelText.trim();

  const resolvedAccessibilityLabel =
    accessibilityLabel ||
    (shouldShowStatus ? `${labelText}, ${normalizedStatus}` : labelText);

  const combinedStyle = [
    styles.base,
    style.base,
    disabled && styles.disabledBase,
  ];

  const textContainerStyle = [
    styles.textContainer,
    shouldShowStatus
      ? styles.textContainerMultiLine
      : styles.textContainerSingleLine,
    !isLastElement && styles.nonTerminal,
  ];

  const arrowIconStyle = [styles.arrowIcon, style.arrowIcon];

  const handlePress = generateCallback({
    func: (payload?: any) => {
      if (disabled) {
        return;
      }

      onPress({
        triggerRef: internalRef,
        ...payload,
      });
    },
  });

  const isInteractive = !disabled && onPress !== noop;
  const shouldOuterBeAccessible =
    !isInteractive && (isProfileBusinessSection || !isScreenReaderEnabled);

  return (
    <View accessible={shouldOuterBeAccessible}>
      <KeyboardFocusable
        viewRef={internalRef}
        accessible={accessible}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={resolvedAccessibilityLabel}
        accessibilityHint={accessibilityHint}
        accessibilityState={{disabled}}
        disabled={disabled}
        style={combinedStyle}
        disableFocusRing={Platform.OS === 'web'}
        onPress={handlePress}
        onLayout={onLayout}
        onFocus={onFocus}
        onBlur={onBlur}
        testID={testID}>
        {!isEmpty(iconName) && (
          <View
            style={styles.iconContainer}
            pointerEvents="none"
            testID="profile-attribute-icon">
            <Icon
              name={iconName}
              style={[
                styles.icon,
                iconName === 'globe' && styles.globeIcon,
                {fontSize: iconName === 'globe' ? scaledGlobeIcon : scaledIcon},
              ]}
              accessible={false}
            />
          </View>
        )}

        <View style={textContainerStyle}>
          <View style={styles.textContent}>
            <Text
              allowFontScaling={true}
              maxFontSizeMultiplier={2}
              useCompetency={useCompetency}
              accessible={false}
              style={styles.labelText}
              testID="profile-attribute-text">
              {label}
            </Text>

            {shouldShowStatus && (
              <Text
                allowFontScaling={true}
                maxFontSizeMultiplier={2}
                testID="profile-attribute-sub-text"
                useCompetency={useCompetency}
                accessible={false}
                style={styles.statusText}
                isBold>
                {status}
              </Text>
            )}
          </View>

          <View style={styles.arrowContainer} pointerEvents="none">
            <Icon
              accessible={false}
              name="chevron-right"
              style={[arrowIconStyle, {fontSize: scaledArrowIcon}]}
            />
          </View>
        </View>
      </KeyboardFocusable>
    </View>
  );
};

export default ProfileActiveAttribute;
